import { z } from 'zod';

import { useChannelSchema } from './channel-schema';
import { useJobDetailsSchema } from './job-details-schema';

/**
 * Main hook for creating the complete validation schema for job posting form
 *
 * Combines:
 * - Channel validation (budgets, dates, allocations, budget type). Pool is
 *   stored as a regular channel entry under `channels.pool` with pool-specific
 *   fields in `poolDetails`; no separate `pool` top-level field.
 * - Job details validation (title, description, stateCode when remote + ZipRecruiter,
 *   server-managed fields when at least one Vonq channel selects the opening,
 *   and VONQ posting requirement required-field checks via `superRefine` in
 *   `job-details-schema.ts` — field definitions are stored in
 *   `vonqPostingRequirementsDefs` inside RHF so the schema can validate them
 *   without external reactive state).
 *
 * @param isZipRecruiterSelected - Passed from the form's parent so the schema can
 *   validate `stateCode` without reaching into context.
 * @param vonqSelectedOpeningIds - Set of opening ids that appear in any Vonq channel's
 *   `openingBudgets`. Drives conditional required-checks for the server-managed
 *   job-detail fields (industry, jobHours, jobType, experience, locationAddress).
 */
export function useValidationSchema(
  isZipRecruiterSelected = false,
  vonqSelectedOpeningIds: ReadonlySet<string> = new Set(),
) {
  return z.object({
    channels: useChannelSchema(),
    jobDetails: useJobDetailsSchema(
      isZipRecruiterSelected,
      vonqSelectedOpeningIds,
    ),
  });
}
